import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FONT_MONO, FONT_SANS, FONT_SERIF, IMG } from '../landing/landingData';
import '../landing/landing.css';

/**
 * The auth dialog from the HyVay handoff design (login / sign up / reset
 * password / check your inbox), rendered as a full page: the design shows it as
 * a card over the dimmed landing page; here each auth route paints the same
 * dotted, glowing backdrop itself so /login, /signup, OAuth redirects and
 * bookmarks keep working unchanged.
 */

const THEME_KEY = 'hyvay:landing-theme';
type Theme = 'light' | 'dark';

function readTheme(): Theme {
    try {
        const v = localStorage.getItem(THEME_KEY);
        if (v === 'dark' || v === 'light') return v;
    } catch { /* storage unavailable */ }
    return 'light';
}

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);

export const AuthShell: React.FC<{ children: React.ReactNode; onClose?: () => void }> = ({ children, onClose }) => {
    const navigate = useNavigate();
    const [theme] = useState<Theme>(readTheme);
    const close = onClose ?? (() => navigate('/'));
    return (
        <div className="hv-root" data-hv-theme={theme} style={{ fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-page)', minHeight: '100vh', position: 'relative', overflowX: 'clip', WebkitFontSmoothing: 'antialiased' }}>
            {/* page backdrop: the landing's dotted sheet, dimmed like the design's modal scrim */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.85, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%,black,transparent)' }} />
            <div style={{ position: 'absolute', top: -200, left: '50%', width: 820, height: 480, marginLeft: -410, borderRadius: '50%', background: 'radial-gradient(closest-side,color-mix(in srgb,var(--brand,#13422C) 10%,transparent),transparent)', filter: 'blur(34px)', pointerEvents: 'none' }} />
            <div style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(20,21,22,0.62)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflow: 'auto' }} onClick={close}>
                {/* margin:auto (not align-items:center) so a card taller than the
                    viewport scrolls instead of having its top clipped. */}
                <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ position: 'relative', margin: 'auto', width: '100%', maxWidth: 452, overflow: 'hidden', borderRadius: 26, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 60px 90px -40px rgba(0,0,0,0.5)', animation: 'hvPop 0.4s cubic-bezier(0.2,0.9,0.24,1) both' }}>
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.75, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 70% 40% at 50% 0%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 70% 40% at 50% 0%,black,transparent)' }} />
                    <div style={{ position: 'absolute', top: -170, left: '50%', width: 420, height: 300, marginLeft: -210, borderRadius: '50%', background: 'radial-gradient(closest-side,color-mix(in srgb,var(--brand,#13422C) 16%,transparent),transparent)', filter: 'blur(30px)', pointerEvents: 'none' }} />

                    <button type="button" onClick={close} aria-label="Close" className="hv-h-mint" style={{ position: 'absolute', zIndex: 2, top: 20, right: 20, width: 34, height: 34, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    </button>

                    <div style={{ position: 'relative', zIndex: 1, padding: '34px 34px 30px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--hv-ink)' }}>
                            {theme === 'dark' ? (
                                <img src={IMG.logoDark} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover' }} />
                            ) : (
                                <img src={IMG.logoLight} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover', mixBlendMode: 'multiply' }} />
                            )}
                            <span style={{ fontFamily: FONT_SANS, fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>HyVay</span>
                        </div>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---- building blocks shared by the four auth views ---------------------------

export const AuthTitle: React.FC<{ lead: string; accent: string; marginTop?: number }> = ({ lead, accent, marginTop = 26 }) => (
    <h2 style={{ margin: `${marginTop}px 0 0`, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 38, lineHeight: 1.02, letterSpacing: '-0.02em' }}>
        {lead} <span style={{ fontStyle: 'italic' }}>{accent}</span>
    </h2>
);

export const AuthLead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>{children}</p>
);

interface FieldProps {
    label: React.ReactNode;
    type?: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    error?: string | null;
    autoComplete?: string;
    autoFocus?: boolean;
    name?: string;
    /** Rendered on the right of the label row (e.g. "Forgot password?"). */
    trailing?: React.ReactNode;
    optional?: boolean;
}

export const Field: React.FC<FieldProps> = ({ label, type = 'text', value, onChange, placeholder, error, autoComplete, autoFocus, name, trailing, optional }) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: trailing ? 10 : 8, fontSize: 12.5, fontWeight: 700, letterSpacing: '-0.005em', color: 'var(--hv-ink)' }}>
            {label}
            {optional && <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 400, letterSpacing: '0.12em', color: 'var(--hv-muted)' }}>OPTIONAL</span>}
            {trailing && <><span style={{ flex: 1 }} />{trailing}</>}
        </span>
        <input
            className="hv-input"
            type={type}
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            aria-invalid={!!error}
            style={{ height: 46, padding: '0 16px', borderRadius: 14, border: `1px solid ${error ? 'var(--hv-danger)' : 'var(--hv-line)'}`, background: 'var(--hv-page-2)', color: 'var(--hv-ink)', fontFamily: 'inherit', fontSize: 14, outline: 'none', boxSizing: 'border-box', width: '100%' }}
        />
        {error && <span style={{ display: 'block', fontSize: 12, fontWeight: 600, lineHeight: 1.45, color: 'var(--hv-danger)', marginTop: -3 }}>{error}</span>}
    </label>
);

export const PrimaryButton: React.FC<{ children: React.ReactNode; type?: 'submit' | 'button'; onClick?: () => void; disabled?: boolean; marginTop?: number }> = ({ children, type = 'submit', onClick, disabled, marginTop = 20 }) => (
    <button type={type} onClick={onClick} disabled={disabled} className="hv-h-brand-82" style={{ marginTop, width: '100%', height: 48, borderRadius: 999, border: 0, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1 }}>
        {children}
    </button>
);

export const GhostButton: React.FC<{ children: React.ReactNode; onClick: () => void; marginTop?: number }> = ({ children, onClick, marginTop = 18 }) => (
    <button type="button" onClick={onClick} className="hv-h-mint-bd" style={{ marginTop, width: '100%', height: 46, borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
        {children}
    </button>
);

export const TextLink: React.FC<{ to?: string; onClick?: () => void; children: React.ReactNode; muted?: boolean }> = ({ to, onClick, children, muted }) => (
    <a
        href={to ?? '#'}
        onClick={(e) => { if (onClick) { e.preventDefault(); onClick(); } }}
        className="hv-h-underline"
        style={muted ? { color: 'var(--hv-body)', fontWeight: 600 } : { fontWeight: 700, color: 'var(--hv-brand-ink)' }}
    >
        {children}
    </a>
);

export const FootNote: React.FC<{ children: React.ReactNode; marginTop?: number }> = ({ children, marginTop = 22 }) => (
    <p style={{ margin: `${marginTop}px 0 0`, textAlign: 'center', fontSize: 13.5, color: 'var(--hv-body)' }}>{children}</p>
);

export const BackToLogin: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button type="button" onClick={onClick} className="hv-h-brand-ink" style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 7, height: 30, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-body)', fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.14em', cursor: 'pointer' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5l-7 7 7 7M4 12h16" /></svg>
        BACK TO LOG IN
    </button>
);

/** Server-side failure (wrong password, expired link…) in the design's danger palette. */
export const DangerCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div role="alert" style={{ marginTop: 18, padding: '12px 14px', borderRadius: 14, background: 'var(--hv-danger-bg)', color: 'var(--hv-danger)', border: '1px solid color-mix(in srgb,var(--hv-danger) 30%,transparent)', fontSize: 13, lineHeight: 1.55, fontWeight: 600 }}>
        {children}
    </div>
);

/** Mint callout (design: "Check your inbox"). */
export const MintCallout: React.FC<{ children: React.ReactNode; icon?: 'mail' | 'check' }> = ({ children, icon = 'mail' }) => (
    <div style={{ marginTop: 20, display: 'flex', gap: 13, padding: 18, borderRadius: 18, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', border: '1px solid color-mix(in srgb,var(--brand,#13422C) 18%,transparent)' }}>
        <span style={{ flex: 'none', width: 34, height: 34, borderRadius: 999, background: 'color-mix(in srgb,var(--brand,#13422C) 14%,transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon === 'mail' ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.6 6.6 12 13l8.4-6.4" /></svg>
            ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.8l4.4 4.2L19 7" /></svg>
            )}
        </span>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, ...tw('pretty') }}>{children}</p>
    </div>
);

// ---- password rules (design: "PASSWORD NEEDS") ---------------------------------

export interface PassRule { label: string; ok: boolean }

export function passwordRules(pw: string): PassRule[] {
    return [
        { label: 'At least 8 characters', ok: pw.length >= 8 },
        { label: 'Upper and lower case', ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw) },
        { label: 'At least one number', ok: /[0-9]/.test(pw) },
        { label: 'A special character', ok: /[^A-Za-z0-9]/.test(pw) },
    ];
}

export const PasswordRules: React.FC<{ rules: PassRule[]; hasError: boolean }> = ({ rules, hasError }) => (
    <div style={{ marginTop: 16, padding: '16px 18px', borderRadius: 18, background: 'var(--hv-page-2)', border: `1px solid ${hasError ? 'color-mix(in srgb, var(--hv-danger) 45%, transparent)' : 'var(--hv-line)'}` }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--hv-muted)' }}>PASSWORD NEEDS</div>
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {rules.map((r) => (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: r.ok ? 'var(--hv-mint-ink)' : hasError ? 'var(--hv-danger)' : 'var(--hv-muted)' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', opacity: r.ok || hasError ? 1 : 0.6 }}>
                        <path d={r.ok ? 'M5 12.8l4.4 4.2L19 7' : 'M6 6l12 12M18 6L6 18'} />
                    </svg>
                    {r.label}
                </div>
            ))}
        </div>
    </div>
);

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function emailError(v: string): string | null {
    if (!v.trim()) return 'Enter your email address.';
    if (!EMAIL_RE.test(v.trim())) return "That doesn't look like a valid email.";
    return null;
}
